import { useIsFocused } from "expo-router/react-navigation";
import { router } from 'expo-router';
import { useLayoutEffect, useState } from 'react';

import { useNpoOnboarding } from '@/api/hooks/meHooks';
import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import NpoGeneralForm from '@/components/forms/NpoGeneralForm';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { GeneralData } from '@/lib/validations/NpoOnboardingSchema';
import { safeJsonParse } from '@/utils/jsonParse';
import { FileToUpload, uploadMultipleToFireStorage } from '@/utils/uploadMultipleToFireStorage';

const GeneralDataScreen = () => {
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage, resetCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useNpoOnboarding(
    authState.signUpData?.loginEmail?.type ?? 'CHURCH'
  );
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();

  const { showError } = useEndFlowModal();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    if (isFocused) authState.signUpData?.loginEmail?.type === 'CHURCH' ? resetCurrentPage() : setCurrentPage(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');

  const updateNonProfit = ({
    name,
    description,
    email,
    address,
    location,
    totalEmployees,
    totalVolunteers,
    showMap,
    thumbnail
  }: {
    name: string;
    description: string | undefined;
    email: string | undefined;
    address: string;
    location: string;
    totalEmployees: number;
    totalVolunteers: number;
    showMap: boolean | undefined;
    thumbnail: string[] | null;
  }) => {
    nonProfitUpdate(
      {
        npoId: authState.signUpData?.loginEmail?.npoId ?? '',
        name: name,
        description: description,
        email: email,
        address: address,
        location: location,
        totalEmployees: totalEmployees,
        totalVolunteers: totalVolunteers,
        showMap: showMap ?? undefined,
        thumbnail: thumbnail
      },
      {
        onSuccess: (_) => {
          onBoardingUpdate(
            {
              preferenceId: authState.preferenceId ?? '',
              general: true
            },
            {
              onSuccess: () => {
                setIsLoading(false);
                setFileProgress(undefined);
                router.navigate('/(app)/login/nonProfit/onBoarding/revenue');
              },
              onError: (e) => {
                setIsLoading(false);
                console.error('Error', e);
              }
            }
          );
        },
        onError: (e) => {
          setIsLoading(false);
          console.error('Error', e);
        }
      }
    );
  };

  const onSubmit = async (data: GeneralData) => {
    try {
      setIsLoading(true);
      let filesToUpload: FileToUpload[] = [];
      if (data.thumbnail) {
        data.thumbnail.map((thumb, index) => {
          if (thumb) {
            filesToUpload.push({
              uri: thumb,
              name: `thumb_${index}.${new Date().toISOString()}`
            });
          }
        });
      }

      if (filesToUpload.length > 0) {
        await uploadMultipleToFireStorage(
          `non-profits-orgs/${authState.signUpData?.loginEmail?.npoId}/thumbs/`,
          filesToUpload,
          (_, progress) => {
            setFileProgress(progress);
          }
        ).then((storagedFiles) => {
          const mappedFiles = storagedFiles.map((result) => result.metadata.fullPath);
          updateNonProfit({
            name: data.name,
            description: data.description,
            email: data.email,
            address: data.address,
            location: data.location,
            totalEmployees: data.totalEmployees,
            totalVolunteers: data.totalVolunteers,
            showMap: data.showMap ?? undefined,
            thumbnail: mappedFiles
          });
        });
      } else {
        updateNonProfit({
          name: data.name,
          description: data.description,
          email: data.email,
          address: data.address,
          location: data.location,
          totalEmployees: data.totalEmployees,
          totalVolunteers: data.totalVolunteers,
          showMap: data.showMap ?? undefined,
          thumbnail: null
        });
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      showError({});
      console.error(e);
    }
  };

  return (
    <NpoGeneralForm
      nonProfitOrg={nonProfitOrg}
      isLoading={onBoardingIsPending || isLoading || nonProfitIsPending}
      fileProgress={fileProgress}
      onSubmitTapped={onSubmit}
    />
  );
};

export default GeneralDataScreen;
